import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import UsdaSearchDialog from "./UsdaSearchDialog";

const empty = { name: "", category: "", calories: 0, protein: 0, carbs: 0, fat: 0, fibre: 0, serving_size: 100, unit: "g", notes: "" };

export default function Foods() {
  const [foods, setFoods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = (search = "") => {
    setLoading(true);
    api.get(`/foods${search ? `?q=${encodeURIComponent(search)}` : ""}`)
      .then(({ data }) => setFoods(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    load(q);
  };

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (f) => { setEditing(f); setForm({ ...empty, ...f }); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      ["calories", "protein", "carbs", "fat", "fibre", "serving_size"].forEach((k) => payload[k] = Number(payload[k]) || 0);
      if (editing) await api.put(`/foods/${editing.food_id}`, payload);
      else await api.post(`/foods`, payload);
      toast.success(editing ? "Food updated" : "Food added");
      setOpen(false);
      load(q);
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const del = async (f) => {
    if (!confirm(`Delete ${f.name}?`)) return;
    try {
      await api.delete(`/foods/${f.food_id}`);
      toast.success("Food deleted");
      load(q);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="foods-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">Database</p>
          <h1 className="text-3xl font-bold mt-1">Food & ingredients</h1>
          <p className="text-muted-foreground mt-1">Manage your nutrition database.</p>
        </div>
        <div className="flex items-center gap-2">
          <UsdaSearchDialog onImported={() => load(q)} />
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} className="gap-2 rounded-full" data-testid="add-food-button">
              <Plus size={16} /> Add food
            </Button>
          </DialogTrigger>          <DialogContent className="max-w-lg" data-testid="food-dialog">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit food" : "Add food"}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-2">
              <div className="space-y-2 col-span-2">
                <Label>Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="food-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="food-category-input" />
              </div>
              <div className="space-y-2">
                <Label>Calories</Label>
                <Input type="number" value={form.calories} onChange={(e) => setForm({ ...form, calories: e.target.value })} data-testid="food-calories-input" />
              </div>
              <div className="space-y-2">
                <Label>Protein (g)</Label>
                <Input type="number" value={form.protein} onChange={(e) => setForm({ ...form, protein: e.target.value })} data-testid="food-protein-input" />
              </div>
              <div className="space-y-2">
                <Label>Carbs (g)</Label>
                <Input type="number" value={form.carbs} onChange={(e) => setForm({ ...form, carbs: e.target.value })} data-testid="food-carbs-input" />
              </div>
              <div className="space-y-2">
                <Label>Fat (g)</Label>
                <Input type="number" value={form.fat} onChange={(e) => setForm({ ...form, fat: e.target.value })} data-testid="food-fat-input" />
              </div>
              <div className="space-y-2">
                <Label>Fibre (g)</Label>
                <Input type="number" value={form.fibre} onChange={(e) => setForm({ ...form, fibre: e.target.value })} data-testid="food-fibre-input" />
              </div>
              <div className="space-y-2">
                <Label>Serving size</Label>
                <Input type="number" value={form.serving_size} onChange={(e) => setForm({ ...form, serving_size: e.target.value })} data-testid="food-serving-input" />
              </div>
              <div className="space-y-2">
                <Label>Unit</Label>
                <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} data-testid="food-unit-input" />
              </div>
            </div>
            <Button onClick={save} disabled={saving || !form.name} className="w-full" data-testid="save-food-button">
              {saving ? "Saving…" : editing ? "Update food" : "Add food"}
            </Button>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <form onSubmit={handleSearch} className="flex gap-2 max-w-md" data-testid="foods-search-form">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search foods…" className="pl-9" data-testid="foods-search-input" />
        </div>
        <Button type="submit" variant="outline">Search</Button>
      </form>

      <div className="nt-card overflow-hidden">
        {loading ? <div className="p-8 text-muted-foreground">Loading…</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="foods-table">
              <thead className="bg-muted text-left">
                <tr>
                  <th className="px-4 py-3 font-semibold">Name</th>
                  <th className="px-4 py-3 font-semibold">Category</th>
                  <th className="px-4 py-3 font-semibold text-right">Cal</th>
                  <th className="px-4 py-3 font-semibold text-right">P</th>
                  <th className="px-4 py-3 font-semibold text-right">C</th>
                  <th className="px-4 py-3 font-semibold text-right">F</th>
                  <th className="px-4 py-3 font-semibold">Serving</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {foods.map((f) => (
                  <tr key={f.food_id} className="hover:bg-muted/50" data-testid={`food-row-${f.food_id}`}>
                    <td className="px-4 py-3 font-medium">{f.name}</td>
                    <td className="px-4 py-3 text-muted-foreground">{f.category || "—"}</td>
                    <td className="px-4 py-3 text-right">{f.calories}</td>
                    <td className="px-4 py-3 text-right">{f.protein}</td>
                    <td className="px-4 py-3 text-right">{f.carbs}</td>
                    <td className="px-4 py-3 text-right">{f.fat}</td>
                    <td className="px-4 py-3 text-muted-foreground">{f.serving_size} {f.unit}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => openEdit(f)} className="text-muted-foreground hover:text-primary mr-2" data-testid={`edit-food-${f.food_id}`}><Pencil size={14} /></button>
                      <button onClick={() => del(f)} className="text-muted-foreground hover:text-destructive" data-testid={`delete-food-${f.food_id}`}><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
